import { Asset } from '@stellar/stellar-sdk';
import axios from 'axios';
import BigNumber from 'bignumber.js';

import { POOL_TYPE } from 'constants/amm';

import { hydratePositionsLiquidity, normalizePositions } from 'helpers/amm-concentrated-positions';
import { getEnvClassicAssetData } from 'helpers/assets';
import chunkFunction from 'helpers/chunk-function';
import { getNetworkPassphrase } from 'helpers/env';
import { getSorobanTokenMetadata } from 'helpers/soroban-token-metadata';
import { createAsset, createLumen } from 'helpers/token';
import { getAmmAquaUrl } from 'helpers/url';

import { AssetsService, SorobanService } from 'services/globalServices';

import {
    FindSwapPath,
    ListResponse,
    NativePrice,
    Pool,
    PoolBalance,
    PoolEvent,
    PoolExtended,
    PoolIncentives,
    PoolProcessed,
    PoolRewards,
    PoolRewardsInfo,
    PoolStatistics,
    PoolUser,
    PoolUserProcessed,
    PoolVolume24h,
    RewardType,
    TokenStats,
    UserReward,
} from 'types/amm';
import { ClassicToken, SorobanToken, Token, TokenType } from 'types/token';

import { AllTimeStats } from './amm.types';

export enum FilterOptions {
    all = 'all',
    stable = 'stable',
    constant = 'volatile',
    concentrated = 'concentrated',
}

const FilterOptionsMap = {
    [FilterOptions.all]: '',
    [FilterOptions.stable]: 'stable',
    [FilterOptions.constant]: 'constant_product',
    [FilterOptions.concentrated]: 'concentrated',
};

export enum PoolsSortFields {
    liquidityUp = '-liquidity',
    liquidityDown = 'liquidity',
    volumeUp = '-volume',
    volumeDown = 'volume',
    totalApyUp = '-total_apy',
    totalApyDown = 'total_apy',
}

const resolvedTokensCache = new Map<string, Token>();
const tokensInFlight = new Map<string, Promise<Token>>();

const normalizeSorobanToken = (token: SorobanToken): SorobanToken => {
    const { code, name } = getSorobanTokenMetadata(token);

    return { ...token, code, name };
};

export const createSorobanTokenStub = (contract: string): SorobanToken =>
    normalizeSorobanToken({
        contract,
        type: TokenType.soroban,
        name: '',
        code: '',
        decimal: 7,
    });

export const convertNativePriceToToken = (item: NativePrice): Token => {
    if (item.name === 'native' && item.is_sac) {
        return createLumen();
    }

    if (item.is_sac) {
        return createAsset(item.code, item.issuer);
    }

    return normalizeSorobanToken({
        contract: item.address,
        type: TokenType.soroban,
        name: item.name,
        code: item.code,
        decimal: item.decimals,
        home_domain: item.home_domain ?? undefined,
        logo: item.logo ?? undefined,
        description: item.description ?? undefined,
        asset_name: item.asset_name ?? undefined,
    });
};

const resolveTokenBatch = async (contractIds: string[]): Promise<Map<string, Token>> => {
    const tokens = new Map<string, Token>();

    try {
        const baseUrl = getAmmAquaUrl();
        const { data } = await axios.get<ListResponse<NativePrice>>(
            `${baseUrl}/tokens/?address__in=${contractIds.join(',')}&size=${contractIds.length}`,
        );

        data.items.forEach(item => {
            if (!contractIds.includes(item.address)) {
                return;
            }

            try {
                tokens.set(item.address, convertNativePriceToToken(item));
            } catch {
                // Fall back to RPC for this malformed backend entry.
            }
        });
    } catch {
        // Fall back to RPC for each contract in the failed batch.
    }

    const missing = contractIds.filter(contractId => !tokens.has(contractId));

    if (missing.length) {
        // Batched inside TokenContract: one simulation per batch, in-memory
        // cache and in-flight dedupe.
        const parsed = await SorobanService.token
            .parseTokenContractIds(missing)
            .catch(() => missing.map((): Token => null));

        missing.forEach((contractId, index) => {
            const token = parsed[index];

            if (!token) {
                tokens.set(contractId, createSorobanTokenStub(contractId));
                return;
            }

            tokens.set(
                contractId,
                token.type === TokenType.soroban ? normalizeSorobanToken(token) : token,
            );
        });
    }

    return tokens;
};

export const resolveTokensByContractIds = async (contractIds: string[]): Promise<Token[]> => {
    const uniqueContractIds = [...new Set(contractIds)];
    const newContractIds = uniqueContractIds.filter(
        contractId => !resolvedTokensCache.has(contractId) && !tokensInFlight.has(contractId),
    );

    for (let index = 0; index < newContractIds.length; index += 50) {
        const batch = newContractIds.slice(index, index + 50);
        const batchPromise = resolveTokenBatch(batch);

        batch.forEach(contractId => {
            const tokenPromise: Promise<Token> = batchPromise
                .then(tokens => tokens.get(contractId) ?? createSorobanTokenStub(contractId))
                .catch(() => createSorobanTokenStub(contractId))
                .then(token => {
                    resolvedTokensCache.set(contractId, token);
                    SorobanService.token.cacheTokens([token]);
                    return token;
                })
                .finally(() => {
                    if (tokensInFlight.get(contractId) === tokenPromise) {
                        tokensInFlight.delete(contractId);
                    }
                });
            tokensInFlight.set(contractId, tokenPromise);
        });
    }

    const tokens = await Promise.all(
        contractIds.map(
            contractId =>
                resolvedTokensCache.get(contractId) ??
                tokensInFlight.get(contractId) ??
                Promise.resolve(createSorobanTokenStub(contractId)),
        ),
    );

    SorobanService.token.cacheTokens(tokens);
    return tokens;
};

export const processPools = async <T extends { tokens_addresses: string[]; tokens_str?: string[] }>(
    pools: T[],
): Promise<(T & { tokens: Token[] })[]> => {
    const contracts: Set<string> = pools.reduce((acc: Set<string>, item: T) => {
        item.tokens_addresses.forEach(id => acc.add(id));
        return acc;
    }, new Set<string>());

    // The backend already sends asset strings (tokens_str) next to contract
    // addresses: classic tokens resolve from them synchronously, so rendering
    // the pools list doesn't wait for RPC. Only pure Soroban tokens are
    // fetched (batched), and only on the first encounter.
    const knownStrings = new Map<string, string>();
    pools.forEach(pool => {
        pool.tokens_addresses.forEach((id, index) => {
            const str = pool.tokens_str?.[index];
            if (str) {
                knownStrings.set(id, str);
            }
        });
    });

    const tokens = (
        await SorobanService.token.parseTokenContractIds([...contracts], knownStrings)
    ).map(token => (token.type === TokenType.soroban ? normalizeSorobanToken(token) : token));

    AssetsService.processAssets(
        tokens.filter(({ type }) => type === TokenType.classic) as ClassicToken[],
    );

    return pools.map(pool => ({
        ...pool,
        tokens: pool.tokens_addresses.map(str => tokens.find(token => token.contract === str)),
    }));
};

export const getPools = async (
    filter: FilterOptions,
    page: number,
    size: number,
    sort: PoolsSortFields,
    search?: string,
): Promise<{ total: number; pools: PoolProcessed[] }> => {
    const capitalizedSearch = (search || '').toUpperCase();
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools/?pool_type=${
            FilterOptionsMap[filter]
        }&sort=${sort}&page=${page}&size=${size}&search=${
            capitalizedSearch === 'XLM' ? 'native' : capitalizedSearch
        }`,
    );
    const processed = await processPools(data.items);
    return { pools: processed, total: data.total };
};

export const getPoolsWithAssets = (assets: (Asset | Token)[]): Promise<PoolProcessed[]> => {
    const baseUrl = getAmmAquaUrl();
    const params = assets.map(asset => SorobanService.token.getAssetContractId(asset)).join(',');

    return axios
        .get<ListResponse<Pool>>(`${baseUrl}/pools/?tokens__in=${params}&size=200`)
        .then(res => processPools(res.data.items));
};

export const getTokenStatsRequest = async (contractId: string): Promise<TokenStats> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<TokenStats>(`${baseUrl}/tokens/${contractId}/stats/`);

    return data;
};
export const getPoolInfo = async (id: string): Promise<PoolProcessed> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<Pool>(`${baseUrl}/pools/${id}/`);
    const [processed] = await processPools([data]);
    return processed;
};

export const getPoolStats = async (id: string): Promise<{ stats: PoolStatistics[] }> => {
    const baseUrl = getAmmAquaUrl();

    try {
        const { data } = await axios.get<ListResponse<PoolStatistics>>(
            `${baseUrl}/statistics/pool/${id}/?size=1000`,
        );
        return {
            stats: data.items
                .filter(({ liquidity, volume }) => Number(liquidity) !== 0 && Number(volume) !== 0)
                .reverse(),
        };
    } catch {
        return { stats: [] };
    }
};

export const getPoolMembers = async (
    id: string,
    page: number,
    size: number,
): Promise<{ members: PoolBalance[]; total: number; page: number }> => {
    const baseUrl = getAmmAquaUrl();

    try {
        const { data } = await axios.get<ListResponse<PoolBalance>>(
            `${baseUrl}/pools/${id}/balances/?sort=-balance&size=${size}&page=${page} `,
        );
        return { members: data.items, total: data.total, page: data.page };
    } catch {
        return { members: [], total: 0, page: 1 };
    }
};

export const getPoolMembersCount = async (id: string): Promise<{ membersCount: number }> => {
    const baseUrl = getAmmAquaUrl();

    try {
        const { data } = await axios.get<ListResponse<PoolBalance>>(
            `${baseUrl}/pools/${id}/balances/?size=1 `,
        );
        return { membersCount: data.total };
    } catch {
        return { membersCount: 0 };
    }
};

export type PoolEventsFilters = {
    eventTypes?: string;
    address?: string;
    ledgerCloseAtGte?: string;
    ledgerCloseAtLte?: string;
};

export const getPoolEvents = async (
    id: string,
    page: number,
    size: number,
    filters?: PoolEventsFilters,
): Promise<{ events: PoolEvent[]; total: number; page: number }> => {
    const baseUrl = getAmmAquaUrl();

    try {
        const { data } = await axios.get<ListResponse<PoolEvent>>(`${baseUrl}/events/pool/${id}/`, {
            params: {
                size,
                page,
                ...(filters?.eventTypes && { event_type__in: filters.eventTypes }),
                ...(filters?.address && { address: filters.address }),
                ...(filters?.ledgerCloseAtGte && {
                    ledger_close_at__gte: filters.ledgerCloseAtGte,
                }),
                ...(filters?.ledgerCloseAtLte && {
                    ledger_close_at__lte: filters.ledgerCloseAtLte,
                }),
            },
        });
        return { events: data.items, total: data.total, page: data.page };
    } catch {
        return { events: [], total: 0, page: 1 };
    }
};

export const getPool = async (id: string): Promise<PoolExtended> => {
    const [info, stats, membersCount] = await Promise.all([
        getPoolInfo(id),
        getPoolStats(id),
        getPoolMembersCount(id),
    ]);
    return Object.assign({}, info, stats, membersCount);
};

export const getUserPools = async (accountId: string): Promise<PoolUserProcessed[]> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<PoolUser>>(
        `${baseUrl}/pools/user/${accountId}/?size=1000`,
    );
    const res = (await processPools(data.items)) as PoolUserProcessed[];
    return res;
};

export const getUserHistory = async (
    accountId: string,
    filter?: string,
): Promise<ListResponse<PoolEvent>> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<PoolEvent>>(
        `${baseUrl}/events/user/${accountId}/`,
        {
            params: {
                size: 500,
                ...(filter && { event_type__in: filter }),
            },
        },
    );

    return data;
};

export const getNextUserHistory = async (link: string): Promise<ListResponse<PoolEvent>> => {
    const { data } = await axios.get<ListResponse<PoolEvent>>(link);

    return data;
};

export const getAmmAquaBalance = async (accountId: string): Promise<number> => {
    const baseUrl = getAmmAquaUrl();
    const { contract: aquaContract, assetString: aquaAssetString } = getEnvClassicAssetData('aqua');

    const { data } = await axios.get<ListResponse<PoolUser>>(
        `${baseUrl}/pools/user/${accountId}/?size=1000&tokens__in=${aquaContract}`,
    );

    const pools = await processPools(data.items);

    const aquaAmounts = await Promise.all(
        pools.map(async item => {
            if (item.pool_type !== POOL_TYPE.concentrated) {
                const aquaIndex = item.tokens_str.findIndex(str => str === aquaAssetString);

                if (aquaIndex < 0 || !Number(item.total_share)) {
                    return new BigNumber(0);
                }

                return new BigNumber(item.reserves[aquaIndex])
                    .div(1e7)
                    .times(new BigNumber(item.balance))
                    .div(new BigNumber(item.total_share));
            }

            try {
                const snapshot = await SorobanService.amm.getUserPositionSnapshot(
                    item.address,
                    accountId,
                );
                const ranges = normalizePositions(snapshot);

                if (!ranges.length) {
                    return new BigNumber(0);
                }

                const hydrated = await hydratePositionsLiquidity(ranges, async range => {
                    const position = await SorobanService.amm.getPosition(
                        item.address,
                        accountId,
                        range.tickLower,
                        range.tickUpper,
                    );

                    return position?.liquidity;
                });

                const positions = hydrated.filter(position =>
                    new BigNumber(position.liquidity || '0').gt(0),
                );

                if (!positions.length) {
                    return new BigNumber(0);
                }

                const positionAmounts = await Promise.all(
                    positions.map(async position => {
                        try {
                            const withdrawTokens = [...item.tokens];
                            const feeTokens = [...item.tokens];
                            const [withdrawEstimates, feeEstimates] = await Promise.all([
                                SorobanService.amm.estimateWithdrawPosition(
                                    accountId,
                                    item.address,
                                    withdrawTokens,
                                    position.tickLower,
                                    position.tickUpper,
                                    String(position.liquidity || '0'),
                                ),
                                SorobanService.amm.getPositionFees(
                                    item.address,
                                    accountId,
                                    feeTokens,
                                    position.tickLower,
                                    position.tickUpper,
                                ),
                            ]);
                            const withdrawAquaIndex = withdrawTokens.findIndex(
                                token => token.contract === aquaContract,
                            );
                            const feeAquaIndex = feeTokens.findIndex(
                                token => token.contract === aquaContract,
                            );
                            const withdrawAmount = new BigNumber(
                                withdrawAquaIndex >= 0
                                    ? withdrawEstimates[withdrawAquaIndex] || '0'
                                    : '0',
                            );
                            const feeAmount = new BigNumber(
                                feeAquaIndex >= 0 ? feeEstimates[feeAquaIndex] || '0' : '0',
                            );

                            return BigNumber.maximum(withdrawAmount.minus(feeAmount), 0);
                        } catch {
                            return new BigNumber(0);
                        }
                    }),
                );

                return positionAmounts.reduce((acc, amount) => acc.plus(amount), new BigNumber(0));
            } catch {
                return new BigNumber(0);
            }
        }),
    );

    const aquaSum: BigNumber = aquaAmounts.reduce(
        (acc, amount) => acc.plus(amount),
        new BigNumber(0),
    );

    return aquaSum.toNumber();
};

export const findSwapPath = async (
    baseId: string,
    counterId: string,
    amount: string | number,
    isSend: boolean,
    decimals: number,
): Promise<FindSwapPath> => {
    const headers = { 'Content-Type': 'application/json' };
    const baseUrl = getAmmAquaUrl();

    const body = JSON.stringify({
        token_in_address: baseId,
        token_out_address: counterId,
        amount: new BigNumber(amount).times(Math.pow(10, decimals)).toFixed(),
    });
    const { data } = await axios.post<FindSwapPath>(
        isSend ? `${baseUrl}/pools/find-path/` : `${baseUrl}/pools/find-path-strict-receive/`,
        body,
        {
            headers,
        },
    );
    return data;
};

export const getTotalStats = async (pageSize = 365): Promise<PoolStatistics[]> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<PoolStatistics>>(
        `${baseUrl}/statistics/totals/?size=${pageSize}`,
    );
    return data.items.reverse();
};

export const getVolume24h = async (): Promise<PoolVolume24h> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<PoolVolume24h>(`${baseUrl}/statistics/24h/`);
    return data;
};

interface GetNativePricesOpts {
    onlySoroban: boolean;
}

export const getNativePrices = async (
    opts?: GetNativePricesOpts,
): Promise<Map<string, { price: string; token: Token }>> => {
    const baseUrl = getAmmAquaUrl();

    const allPrices = new Map<string, { price: string; token: Token }>();

    const { data } = await axios.get<ListResponse<NativePrice>>(
        `${baseUrl}/tokens/?pooled=true&size=500`,
    );
    const prices = data.items.filter(item => {
        if (opts?.onlySoroban) {
            return !item.is_sac;
        }

        return true;
    });

    prices.forEach(price => {
        allPrices.set(price.address, {
            price: (+price.price_xlm * Math.pow(10, price.decimals - 7)).toFixed(7),
            token: convertNativePriceToToken(price),
        });
    });

    return allPrices;
};

export const getPathPoolsFee = async (addresses: Array<string>): Promise<Map<string, string>> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools/?address__in=${addresses.join(',')}`,
    );

    return data.items.reduce((acc, pool) => {
        acc.set(pool.address, pool.fee);
        return acc;
    }, new Map<string, string>());
};

export const getPoolsToMigrate = async (base: Asset, counter: Asset): Promise<Pool[] | null> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools?tokens__in=${SorobanService.token.getAssetContractId(
            base,
        )},${SorobanService.token.getAssetContractId(counter)}`,
    );

    const pools = data.items
        .filter(item => item.tokens_str.length === 2)
        .sort((a, b) => +b.liquidity_usd - +a.liquidity_usd);

    if (!pools.length) {
        return null;
    }
    return processPools(pools);
};

export const getPoolsForIncentives = async (): Promise<PoolProcessed[] | null> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools/?gauge_enabled=true&size=500`,
    );

    return processPools(data.items);
};

// TODO: remove this method when this data is placed on the backend
export const getAmmRewards = async (): Promise<number> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<PoolRewards>>(`${baseUrl}/pool-rewards/`);

    const sumTps = data.items.reduce((acc, item) => acc + Number(item.tps) / 1e7, 0);

    return sumTps * 60 * 60 * 24;
};

// TODO: remove this method when this data is placed on the backend
export const getAquaInPoolsSum = async (): Promise<{ sum: number; sum_usd: number }> => {
    const baseUrl = getAmmAquaUrl();

    const { contract: aquaContract, assetString: aquaAssetString } = getEnvClassicAssetData('aqua');

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools/?&search=${aquaContract}&size=500`,
    );

    return data.items.reduce(
        (acc, item) => {
            const aquaIndex = item.tokens_str.findIndex(str => str === aquaAssetString);

            acc.sum = acc.sum + Number(item.reserves[aquaIndex]) / 1e7;
            acc.sum_usd = acc.sum_usd + Number(item.liquidity_usd) / 1e7 / 2;

            return acc;
        },
        { sum: 0, sum_usd: 0 },
    );
};

// TODO: remove this method when this data is placed on the backend
export const getAquaPoolsMembers = async (): Promise<number> => {
    const baseUrl = getAmmAquaUrl();

    const { contract: aquaContract } = getEnvClassicAssetData('aqua');

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools/?&search=${aquaContract}&size=500`,
    );

    const poolsId = data.items.map(item => item.address);

    const membersCount = await Promise.all(poolsId.map(id => getPoolMembersCount(id)));

    return membersCount.reduce((acc, item) => acc + item.membersCount, 0);
};

export const getAquaXlmRate = async (): Promise<number[]> => {
    const baseUrl = getAmmAquaUrl();

    const { contract: aquaContract } = getEnvClassicAssetData('aqua');

    const XLM_CONTRACT = Asset.native().contractId(getNetworkPassphrase());

    const { data } = await axios.get<ListResponse<Pool>>(
        `${baseUrl}/pools/?&tokens__in=${aquaContract},${XLM_CONTRACT}`,
    );

    const [bestPool] = data.items.sort((a, b) => Number(b.liquidity_usd) - Number(a.liquidity_usd));

    return bestPool.reserves.map(reserve => +reserve / 1e7);
};

// Later lookups by contract (resolveTokensByContractIds, SorobanService.token) reuse these
const cacheResolvedTokens = (tokens: Token[]) => {
    tokens.forEach(token => resolvedTokensCache.set(token.contract, token));
    SorobanService.token.cacheTokens(tokens);
};

// TODO: remove this method when this data is placed on the backend
export const getAssetsList = async (): Promise<Token[]> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<NativePrice>>(
        // sort=-tvl: order by TVL desc so legit tokens come first and spam/dust
        // (low-liquidity tokens with low-sorting contract addresses) falls to the bottom
        `${baseUrl}/tokens/?pooled=true&size=200&sort=-tvl`,
    );

    // tokens in the top of the list - AQUA, XLM, USDC
    const { asset: aquaStellarAsset } = getEnvClassicAssetData('aqua');
    const { asset: usdcStellarAsset } = getEnvClassicAssetData('usdc');
    const lumen = createLumen();

    const otherTokens = data.items
        .filter(
            ({ address }) =>
                address !== aquaStellarAsset.contract &&
                address !== usdcStellarAsset.contract &&
                address !== lumen.contract,
        )
        .map(price => convertNativePriceToToken(price));

    const tokens = [aquaStellarAsset, lumen, usdcStellarAsset, ...otherTokens];
    cacheResolvedTokens(tokens);

    return tokens;
};

// Top 30 tokens by TVL whose ticker or name contains the text, or whose home domain,
// contract or CODE:ISSUER equals it. Not limited to pooled tokens, so a token without
// a pool can still be picked to create one.
export const searchTokens = async (search: string): Promise<Token[]> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<ListResponse<NativePrice>>(
        `${baseUrl}/tokens/?search=${encodeURIComponent(search)}&size=30&sort=-tvl`,
    );

    const tokens = data.items.map(price => convertNativePriceToToken(price));
    cacheResolvedTokens(tokens);

    return tokens;
};

function chunkArray<T>(arr: T[], size = 5) {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
    }
    return result;
}

// Builds the claimable reward entries (AQUA rewards, incentives, position fees) for a single pool.
// Reused both for the full all-pools scan and for the precomputed list built from My Liquidity.
export const buildPoolUserRewards = (
    pool: { address: string; tokens: Token[]; pool_type: POOL_TYPE; fee: string },
    rewardInfo: PoolRewardsInfo | undefined,
    incentives: PoolIncentives[] | undefined,
    fees: string[] | undefined,
): UserReward[] => {
    const list: UserReward[] = [];

    if (rewardInfo && Number(rewardInfo.to_claim)) {
        list.push({
            id: `${RewardType.aquaReward}-${pool.address}`,
            amount: Number(rewardInfo.to_claim),
            tokens: pool.tokens,
            poolType: pool.pool_type,
            fee: pool.fee,
            poolAddress: pool.address,
            type: RewardType.aquaReward,
        });
    }

    if (incentives?.length && incentives.some(incentive => !!Number(incentive.info.user_reward))) {
        list.push({
            id: `${RewardType.incentive}-${pool.address}`,
            poolAddress: pool.address,
            incentives,
            tokens: pool.tokens,
            poolType: pool.pool_type,
            fee: pool.fee,
            type: RewardType.incentive,
        });
    }

    if (fees?.some(amount => Number(amount) > 0)) {
        list.push({
            id: `${RewardType.positionFees}-${pool.address}`,
            poolAddress: pool.address,
            fees: pool.tokens
                .map((token, index) => ({ token, amount: fees[index] || '0' }))
                .filter(({ amount }) => Number(amount) > 0),
            tokens: pool.tokens,
            poolType: pool.pool_type,
            fee: pool.fee,
            type: RewardType.positionFees,
        });
    }

    return list;
};

export const sortUserRewards = (list: UserReward[]): UserReward[] => {
    const getSortValue = (reward: UserReward): number => {
        if (reward.type === RewardType.aquaReward) {
            return reward.amount;
        }
        if (reward.type === RewardType.incentive) {
            return Number(reward.incentives[0].info.user_reward);
        }
        return Number(reward.fees[0]?.amount ?? 0);
    };

    return [...list].sort((a, b) => getSortValue(b) - getSortValue(a));
};

export const getUserRewardsList = async (accountId: string): Promise<UserReward[]> => {
    const baseUrl = getAmmAquaUrl();

    // Fees are auto-claimed on withdraw, so they only exist on pools the user currently holds.
    // We simulate fees only for those, instead of every concentrated pool in the system.
    const [{ data }, userPools] = await Promise.all([
        axios.get<ListResponse<Pool>>(`${baseUrl}/pools/?size=500`),
        getUserPools(accountId),
    ]);

    const userConcentratedPools = new Set(
        userPools
            .filter(({ pool_type }) => pool_type === POOL_TYPE.concentrated)
            .map(({ address }) => address),
    );

    const results: UserReward[] = [];

    const processed = await processPools(data.items);

    const chunked = chunkArray(processed, 4);

    await chunkFunction(chunked, async chunk => {
        const rewards = await SorobanService.amm.getPoolsRewards(
            accountId,
            chunk.map(({ address }) => address),
        );
        const incentives = await SorobanService.amm.getPoolsIncentives(
            accountId,
            chunk.map(({ address }) => address),
        );

        const fees = await Promise.all(
            chunk.map(pool =>
                pool.pool_type === POOL_TYPE.concentrated && userConcentratedPools.has(pool.address)
                    ? SorobanService.amm
                          .getAllPositionFees(pool.address, accountId, pool.tokens)
                          .catch(() => pool.tokens.map(() => '0'))
                    : Promise.resolve(pool.tokens.map(() => '0')),
            ),
        );

        chunk.forEach((pool, index) => {
            results.push(
                ...buildPoolUserRewards(pool, rewards[index], incentives[index], fees[index]),
            );
        });
    });

    return sortUserRewards(results);
};

export const getAllTimeStats = async (): Promise<AllTimeStats> => {
    const baseUrl = getAmmAquaUrl();

    const { data } = await axios.get<AllTimeStats>(
        `${baseUrl}/api/external/v2/statistics/all-time/`,
    );

    return data;
};
